// ========================================================================================================
// CarrierInviteViewBidPage.jsx
// --------------------------------------------------------------------------------------------------------
// Wrapper that coordinates two carrier-side views that operate over the same Tender (and optionally
// the same Tender Phase):
//
//   1) TenderConfigCrud   : configuration and read-only inspection of the Tender / Phase.
//   2) CarrierSubmitPage  : carrier bid submission against the same Tender / Phase.
//
// It supports two hosting models:
//
//   - Normal navigation inside the carrier portal:
//       * TenderConfigCrud shows the tender list.
//       * The user selects a tender or phase and clicks "Bid".
//       * TenderConfigCrud calls onOpenBid(context, header) and this wrapper opens CarrierSubmitPage.
//
//   - Deep-link invites coming from email links:
//       * The user opens "#/carrier/invite?type=spot&tenderId=...&token=...".
//       * CarrierInviteEntryPage normalizes and persists the invite context.
//       * After login, the user lands on this wrapper.
//       * This wrapper resolves the invite context, validates it against the logged carrier,
//         and opens CarrierSubmitPage directly with the correct mode.
//
// Invite query parameters:
//   - type: "spot" | "phase"
//   - tenderId: number (for spot)
//   - tenderPhaseId: number (for phase)
//   - token: invite JWT issued by the backend
//
// When a valid invite is detected and the carrier is authenticated, this page will:
//   - Call the public CarrierInviteSpot/CarrierInvitePhase Context endpoints.
//   - Verify that the invite CarrierId matches the currently authenticated carrier.
//   - Derive the correct CRUD mode from the invite context:
//       * Tender open + no submission     -> "create"
//       * Tender open + has submission    -> "edit"
//       * Tender finished                 -> "view"
//   - Build a bidContext compatible with CarrierSubmitPage, including Tender/TenderPhase
//     DTOs when available so lanes can be resolved from RouteId.
//   - Open CarrierSubmitPage in bid view.
// ========================================================================================================

import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import TenderConfigCrud from "../Tenders/TenderConfigCrud";
import CarrierSubmitPage from "./CarrierSubmitPage";

import { LoadingContext, LoadingBar } from "src/M_Components";
import { BufferService } from "src/M_Components/M_Utils/buffer.service";
import { useCarrierAuth } from "src/components/auth/CarrierAuthProvider";
import { useTranslation } from "react-i18next";
import {
  isFailurePayload,
  apiMessage,
} from "src/components/utils/apiResponse";
import { STORAGE_CARRIER } from "src/components/auth/storageKeys";

// Key used to persist the invite token (for example to be reused by API clients).
const INVITE_TOKEN_STORAGE_KEY = "carrier_invite_token";

// Key used to persist a normalized "pending invite" context across redirects
// between /carrier/invite, /carrier/login and this wrapper.
const PENDING_INVITE_KEY = "carrier_invite_pending";

/**
 * Helper hook: parse invite-related query params from the current URL.
 *
 * Expected parameters:
 *   - type: "spot" | "phase"
 *   - tenderId: number (for spot)
 *   - tenderPhaseId: number (for phase)
 *   - token: invite JWT issued by the backend
 *
 * Returns either a normalized context object or null when the URL does not contain
 * a valid invite.
 */
function useInviteContextFromQuery() {
  const location = useLocation();

  return useMemo(() => {
    const search = location.search || "";
    const qs = new URLSearchParams(search);

    const rawType = (qs.get("type") || "").toLowerCase(); // "spot" | "phase" | ""
    const tenderIdRaw = qs.get("tenderId");
    const phaseIdRaw = qs.get("tenderPhaseId");
    const token = qs.get("token") || null;

    const isSpot = rawType === "spot";
    const isPhase = rawType === "phase";

    const tenderId = tenderIdRaw ? Number(tenderIdRaw) : null;
    const tenderPhaseId = phaseIdRaw ? Number(phaseIdRaw) : null;

    const hasValidSpot = isSpot && !!tenderId && !!token;
    const hasValidPhase = isPhase && !!tenderPhaseId && !!token;

    if (!hasValidSpot && !hasValidPhase) {
      // Not a valid invite URL -> return null so children can behave as usual.
      return null;
    }

    return {
      // "scope" is aligned with the existing bidContext shape.
      scope: isPhase ? "phase" : "tender",
      // preserve original type just in case ("spot" | "phase")
      type: isPhase ? "phase" : "spot",
      tenderId,
      tenderPhaseId,
      token,
      // Optional timestamp
      createdAt: Date.now(),
      source: "query",
    };
  }, [location.search]);
}

/**
 * Read a pending invite context from localStorage when there is no valid query.
 * This is used when the user came from an email invite, went through /carrier/login,
 * and has been redirected to this wrapper without query string.
 */
function readPendingInviteFromStorage() {
  try {
    const raw = window.localStorage.getItem(PENDING_INVITE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) || {};
    const rawType = (parsed.type || "").toLowerCase();
    const token = parsed.token || null;

    const isSpot = rawType === "spot";
    const isPhase = rawType === "phase";

    const tenderId =
      parsed.tenderId != null ? Number(parsed.tenderId) : null;
    const tenderPhaseId =
      parsed.tenderPhaseId != null ? Number(parsed.tenderPhaseId) : null;

    const hasValidSpot = isSpot && !!tenderId && !!token;
    const hasValidPhase = isPhase && !!tenderPhaseId && !!token;

    if (!hasValidSpot && !hasValidPhase) {
      return null;
    }

    return {
      scope: isPhase ? "phase" : "tender",
      type: isPhase ? "phase" : "spot",
      tenderId,
      tenderPhaseId,
      token,
      createdAt: parsed.createdAt || Date.now(),
      source: "storage",
    };
  } catch {
    return null;
  }
}

/**
 * Try reading the current carrier id from the realm-scoped session storage.
 * This is only used as a fallback when the auth hook cannot provide a carrierId.
 */
function getCarrierIdFromStorage() {
  try {
    const key = STORAGE_CARRIER?.session || "app.auth.carrier.session";
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const cid = parsed?.CarrierId ?? parsed?.carrierId;
    const n = cid != null ? Number(cid) : null;
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * Derive the appropriate CRUD mode for CarrierSubmitPage based on the invite context
 * returned by the backend. This keeps the mode decisions in one place:
 *
 *   - Tender finished              -> "view"
 *   - Tender open + has submission -> "edit"
 *   - Tender open + no submission  -> "create"
 *
 * If the backend does not expose enough information, this helper falls back to the
 * original behavior: "create" when HasSubmitted is false, otherwise "view".
 */
function resolveCrudModeFromInviteResponse(res) {
  const hasSubmitted = !!(res?.HasSubmitted ?? res?.hasSubmitted);

  // First try a direct boolean flag.
  let isFinished = false;
  if (res?.IsFinished != null) {
    isFinished = Boolean(res.IsFinished);
  } else if (res?.isFinished != null) {
    isFinished = Boolean(res.isFinished);
  } else {
    // If there is no explicit boolean flag, try to infer from a status string.
    const rawStatus =
      (res?.Status ||
        res?.status ||
        res?.TenderStatus ||
        res?.tenderStatus ||
        res?.State ||
        res?.state ||
        "") + "";
    const status = rawStatus.toLowerCase().trim();
    if (status) {
      if (
        status.includes("finished") ||
        status.includes("closed") ||
        status.includes("ended") ||
        status.includes("expired")
      ) {
        isFinished = true;
      }
    }
  }

  if (isFinished) {
    return "view";
  }

  if (hasSubmitted) {
    return "edit";
  }

  return "create";
}

export default function CarrierInviteViewBidPage({
  roles,
  onCreateTab,
  handleTabClose,
  toastMessages,

  // Optional: controls which view is rendered first when not coming from invite.
  // - "config" (default) : start in TenderConfigCrud.
  // - "bid"              : attempt to start in CarrierSubmitPage.
  initialView = "config",

  // Optional: when starting in bid view (non-invite), these props provide the initial context
  // used by CarrierSubmitPage. They mirror what TenderConfigCrud would normally
  // prepare when the user presses the "Bid" button.
  initialBidContext = null,
  initialBidHeader = null,

  // Optional: global logout handler provided by TabsHomePage.
  // When present, it will be used in integrity-sensitive cases
  // (e.g. invite carrier mismatch) to force a full logout.
  onForceLogout,
}) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);
  const { isAuthenticated, carrierId: authCarrierId } = useCarrierAuth();

  // Refs to avoid re-running the main effect when these callbacks change identity
  const onCreateTabRef = useRef(onCreateTab);
  const onForceLogoutRef = useRef(onForceLogout);
  const hasOpenedTabFromInviteRef = useRef(false);

  // This ref acts as a guard to ensure the invite context is only processed once
  // when all prerequisites are ready (auth, carrier id, baseUrl).
  const hasProcessedInviteRef = useRef(false);

  useEffect(() => {
    onCreateTabRef.current = onCreateTab;
  }, [onCreateTab]);

  useEffect(() => {
    onForceLogoutRef.current = onForceLogout;
  }, [onForceLogout]);

  // ----------------------------------------------------------------------------------------------------
  // Invite context resolution (query string first, then pending storage)
  // ----------------------------------------------------------------------------------------------------
  const inviteContextFromQuery = useInviteContextFromQuery();
  const [inviteContext, setInviteContext] = useState(inviteContextFromQuery);

  useEffect(() => {
    // Query string takes precedence when present and valid.
    if (inviteContextFromQuery) {
      setInviteContext(inviteContextFromQuery);
      return;
    }

    // When there is no valid query, fall back to the pending invite persisted
    // by CarrierInviteEntryPage during the login flow.
    const fromStorage = readPendingInviteFromStorage();
    if (fromStorage) {
      setInviteContext(fromStorage);
    } else {
      setInviteContext(null);
    }
  }, [inviteContextFromQuery]);

  // Optionally persist the invite token for API clients (for example Axios interceptors).
  useEffect(() => {
    if (inviteContext?.token) {
      try {
        window.localStorage.setItem(
          INVITE_TOKEN_STORAGE_KEY,
          inviteContext.token
        );
      } catch {
        // ignore storage errors
      }
    }
  }, [inviteContext]);

  // ----------------------------------------------------------------------------------------------------
  // Local view state
  // ----------------------------------------------------------------------------------------------------
  const initialViewState =
    initialView === "bid" && initialBidContext?.tenderId ? "bid" : "config";

  const [view, setView] = useState(initialViewState); // "config" | "bid"
  const selectedTab = useSelector((s) =>
    s.StateKeyValue.find((o) => o.key === "selectedTab")?.value
  );

  const handleCloseWrapperTab = useCallback(() => {
    if (handleTabClose && selectedTab != null) {
      handleTabClose(selectedTab);
    }
  }, [handleTabClose, selectedTab]);

  // ----------------------------------------------------------------------------------------------------
  // Bid context snapshot
  // ----------------------------------------------------------------------------------------------------
  const [bidContext, setBidContext] = useState(initialBidContext);
  const [bidHeader, setBidHeader] = useState(initialBidHeader);

  // ----------------------------------------------------------------------------------------------------
  // Derived configSeed used by TenderConfigCrud to know which Tender / TenderPhase to load
  // when coming back from CarrierSubmitPage (especially important after login + full reload).
  // ----------------------------------------------------------------------------------------------------
  const configSeed = useMemo(() => {
    if (!bidContext) return null;

    const scope =
      (bidContext.scope ||
        (bidContext.tenderPhaseId ? "phase" : "tender")).toLowerCase();

    return {
      scope,                                   // "tender" | "phase"
      tenderId: bidContext.tenderId ?? null,
      tenderPhaseId: bidContext.tenderPhaseId ?? null,
    };
  }, [bidContext]);


  // Invite validation state
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState("");

  const baseUrl = ctx?.DEFAULTURL || "";

  // ----------------------------------------------------------------------------------------------------
  // Helper: resolve logged carrier id (auth context first, then storage fallback)
  // ----------------------------------------------------------------------------------------------------
  const loggedCarrierId = useMemo(() => {
    if (authCarrierId != null) {
      const n = Number(authCarrierId);
      if (Number.isFinite(n)) return n;
    }
    return getCarrierIdFromStorage();
  }, [authCarrierId]);

  // ----------------------------------------------------------------------------------------------------
  // Handler: transition from TenderConfigCrud -> CarrierSubmitPage (manual, old flow)
  // ----------------------------------------------------------------------------------------------------
  const handleOpenBid = (context, header) => {
    if (!context || (!context.tenderId && !context.tenderPhaseId)) {
      return;
    }

    setBidContext((prev) => {
      if (!prev) {
        // No previous context = accept the new one as-is
        return context;
      }

      const sameScope = (prev.scope || "").toLowerCase() === (context.scope || "").toLowerCase();
      const sameTender = Number(prev.tenderId || 0) === Number(context.tenderId || 0);
      const samePhase =
        Number(prev.tenderPhaseId || 0) === Number(context.tenderPhaseId || 0);

      if (sameScope && (sameTender || samePhase)) {
        // For the same Tender/Phase, preserve the original crudMode (and hasSubmitted)
        return {
          ...context,
          crudMode: prev.crudMode || context.crudMode || "create",
          hasSubmitted:
            prev.hasSubmitted ?? context.hasSubmitted ?? false,
        };
      }

      // For a different tender/phase/tab, accept the new context as-is
      return context;
    });

    setBidHeader(header || null);
    setView("bid");
  };

  // ----------------------------------------------------------------------------------------------------
  // Handler: transition from CarrierSubmitPage -> TenderConfigCrud
  // ----------------------------------------------------------------------------------------------------
  const handleBackToConfig = () => {
    setView("config");
  };

  // ----------------------------------------------------------------------------------------------------
  // Invite-specific logic:
  //  - Call CarrierInviteSpot/Phase Context endpoints.
  //  - Validate that invite CarrierId matches logged carrier.
  //  - If ok, derive CRUD mode and build a bidContext compatible with CarrierSubmitPage.
  //
  // IMPORTANT:
  //  - This effect is guarded by "hasProcessedInviteRef" so that the invite is
  //    handled only once per valid context, even if context/loader changes upstream
  //    cause re-renders.
  // ----------------------------------------------------------------------------------------------------
  useEffect(() => {
    // No invite = keep existing behavior (config first, onOpenBid later).
    if (!inviteContext) return;

    // Wait until we have a valid auth + carrier + baseUrl.
    if (!isAuthenticated) {
      const msg =
        t("InviteRequiresCarrierLogin") ||
        "You must be logged in as a carrier to use this invite link.";
      // Do not mark as processed here; effect should re-run after auth is ready.
      setInviteError(msg);
      return;
    }

    if (!loggedCarrierId) {
      const msg =
        t("InviteMissingCarrierId") ||
        "Could not resolve the current carrier account.";
      // Do not mark as processed here; effect should re-run once carrier id is available.
      setInviteError(msg);
      return;
    }

    if (!baseUrl) {
      // Again, do not mark as processed so it can be retried once DEFAULTURL is set.
      return;
    }

    // If we already processed this invite context successfully once,
    // avoid re-processing it on subsequent re-renders.
    if (hasProcessedInviteRef.current) {
      return;
    }
    hasProcessedInviteRef.current = true;

    const isPhase = inviteContext.type === "phase";
    const serviceName = isPhase ? "carrier-invite/phase" : "carrier-invite/spot";
    const serviceFullName = `${serviceName}/context?token=${encodeURIComponent(
      inviteContext.token
    )}`;
    const svc = new BufferService(serviceFullName, baseUrl);

    const load = async () => {
      setInviteLoading(true);
      setInviteError("");

      // IMPORTANT:
      // This page already renders a local LoadingBar splash during invite resolution.
      // Using the global MRouter overlay (ctx.setIsLoad) at the same time would show
      // two overlapping loaders (global + local). Therefore, we keep invite flow local-only.
      // ctx?.setIsLoad?.(true);

      try {
        const res = await svc.get();

        if (isFailurePayload(res)) {
          const msg =
            apiMessage(res, t("ErrorLoadingData")) ||
            "Failed to load invite context.";
          setInviteError(msg);
          toastMessages?.("error", "", msg);
          return;
        }

        // Ownership check: invite carrier vs logged carrier.
        const ctxCarrierId = Number(res.CarrierId ?? res.carrierId ?? 0);
        if (!ctxCarrierId || ctxCarrierId !== Number(loggedCarrierId)) {
          const msg =
            t("InviteCarrierMismatchMessage") ||
            "This invite token belongs to a different carrier account.";
          setInviteError(msg);
          toastMessages?.("error", "", msg);

          // Integrity-sensitive scenario: force logout when possible.
          if (typeof onForceLogoutRef.current === "function") {
            onForceLogoutRef.current();
          }

          return;
        }

        // Resolve crudMode based on HasSubmitted and tender status.
        const crudMode = resolveCrudModeFromInviteResponse(res);
        const hasSubmitted = !!(res?.HasSubmitted ?? res?.hasSubmitted);

        // Optional Tender and TenderPhase DTOs coming from the backend.
        // When present, these help CarrierSubmitPage resolve lanes via RouteId.
        const tenderDto = res.Tender || res.tender || null;
        const tenderPhaseDto =
          res.TenderPhase || res.tenderPhase || res.Phase || res.phase || null;

        // -------------------------------------------------------------------
        // Build bidContext + header for phase or tender invite
        // -------------------------------------------------------------------
        if (isPhase) {
          const context = {
            scope: "phase",
            crudMode,
            tenderId: res.TenderId ?? res.tenderId ?? null,
            tenderPhaseId: res.TenderPhaseId ?? res.tenderPhaseId ?? null,
            tenderDto: tenderDto || null,
            tenderPhaseDto: tenderPhaseDto || null,
            lanes: res.Lanes || res.lanes || [],
            hasSubmitted,
          };

          const header = {
            Id: context.tenderPhaseId,
            Name: `${res.RfqName || res.RfqName === "" ? res.RfqName : res.TenderName
              } / ${res.PhaseName || res.phaseName || ""}`,
          };

          setBidContext(context);
          setBidHeader(header);
          setView("bid");

          // Ensure the tab label is properly set when opened via invite link, but only once.
          if (
            inviteContext &&
            !hasOpenedTabFromInviteRef.current &&
            typeof onCreateTabRef.current === "function"
          ) {
            hasOpenedTabFromInviteRef.current = true;

            const baseLabel =
              t("LabelViewTenderPhase") || t("LabelViewTender") || "Tender Phase";
            const extra = tenderPhaseDto?.Name || tenderDto?.Name || header.Name || "";
            const tabLabel = extra ? `${baseLabel} "${extra}"` : baseLabel;

            onCreateTabRef.current("/CarrierInviteViewBid", tabLabel);
          }
        } else {
          const context = {
            scope: "tender",
            crudMode,
            tenderId: res.TenderId ?? res.tenderId ?? null,
            tenderPhaseId: null,
            tenderDto: tenderDto || null,
            tenderPhaseDto: null,
            lanes: res.Lanes || res.lanes || [],
            hasSubmitted,
          };

          const header = {
            Id: context.tenderId,
            Name: res.RfqName || res.TenderName,
          };

          setBidContext(context);
          setBidHeader(header);
          setView("bid");

          // Ensure the tab label is properly set when opened via invite link, but only once.
          if (
            inviteContext &&
            !hasOpenedTabFromInviteRef.current &&
            typeof onCreateTabRef.current === "function"
          ) {
            hasOpenedTabFromInviteRef.current = true;

            const baseLabel = t("LabelViewTender") || "Tender";
            const extra = header.Name || tenderDto?.Name || "";
            const tabLabel = extra ? `${baseLabel} "${extra}"` : baseLabel;

            onCreateTabRef.current("/CarrierInviteViewBid", tabLabel);
          }
        }
      } catch (err) {
        const msg =
          apiMessage(err, t("ErrorLoadingData")) ||
          "Error while loading invite context.";
        setInviteError(msg);
        toastMessages?.("error", "", msg);
      } finally {
        setInviteLoading(false);

        // Keep global overlay untouched in invite flow (local splash is enough).
        // ctx?.setIsLoad?.(false);
      }
    };

    load();
  }, [
    inviteContext,
    isAuthenticated,
    loggedCarrierId,
    baseUrl,
    toastMessages,
    t,
    ctx,
  ]);


  // ----------------------------------------------------------------------------------------------------
  // Render: invite error (when URL has invite or pending invite but validation fails)
  // ----------------------------------------------------------------------------------------------------
  if (inviteContext && inviteError) {
    return (
      <div
        style={{
          padding: 24,
          fontFamily: "sans-serif",
        }}
      >
        <h2>{t("CarrierInvite") || "Carrier Invite"}</h2>
        <p>{inviteError}</p>
      </div>
    );
  }

  // ----------------------------------------------------------------------------------------------------
  // Render: invite loading splash (only when opened via invite)
  //  - Show informational text in the background and a loading spinner on top.
  // ----------------------------------------------------------------------------------------------------
  if (inviteContext && inviteLoading && !bidContext) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "sans-serif",
          color: "#444",
          padding: "16px",
          textAlign: "center",
          position: "relative",
        }}
      >
        {/* Background informational messages behind the loading spinner */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxWidth: "480px",
            opacity: 0.35,
            pointerEvents: "none",
          }}
        >
          <h2 style={{ marginBottom: "8px" }}>
            {t("CarrierInvite") || "Carrier Invite"}
          </h2>
          <p style={{ margin: 0 }}>
            {t("LoadingInviteContext") ||
              t("Loading") ||
              "Loading invite context..."}
          </p>
        </div>

        {/* Foreground loading spinner */}
        <LoadingBar />
      </div>
    );
  }

  // ----------------------------------------------------------------------------------------------------
  // Render: bid view
  //  - For spot: require tenderId
  //  - For phase: allow tenderPhaseId without tenderId
  // ----------------------------------------------------------------------------------------------------
  const canRenderBid =
    bidContext &&
    (bidContext.tenderId ||
      (bidContext.scope === "phase" && bidContext.tenderPhaseId));

  if (view === "bid" && canRenderBid) {
    return (
      <CarrierSubmitPage
        handleTabClose={handleCloseWrapperTab}
        toastMessages={toastMessages}
        // Explicit bid context driven by wrapper
        bidContext={bidContext}
        bidHeader={bidHeader}
        // Invite context origin (query or storage) with token and type
        inviteContext={inviteContext}
        // Back navigation into configuration view
        onBack={handleBackToConfig}
      />
    );
  }

  // ----------------------------------------------------------------------------------------------------
  // Render: configuration view (TenderConfigCrud)
  // ----------------------------------------------------------------------------------------------------
  return (
    <TenderConfigCrud
      roles={roles}
      onCreateTab={onCreateTab}
      handleTabClose={handleTabClose}
      toastMessages={toastMessages}
      isCarrierRealm={true}
      onOpenBid={handleOpenBid}
      inviteContext={inviteContext}
      configSeed={configSeed}
    />
  );
}

CarrierInviteViewBidPage.propTypes = {
  roles: PropTypes.array,
  onCreateTab: PropTypes.func,
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func,
  initialView: PropTypes.oneOf(["config", "bid"]),
  initialBidContext: PropTypes.shape({
    scope: PropTypes.oneOf(["tender", "phase"]),
    crudMode: PropTypes.oneOf(["create", "edit", "view"]),
    tenderId: PropTypes.number,
    tenderPhaseId: PropTypes.number,
    tenderDto: PropTypes.object,
    tenderPhaseDto: PropTypes.object,
    lanes: PropTypes.array,
    hasSubmitted: PropTypes.bool,
  }),
  initialBidHeader: PropTypes.shape({
    Id: PropTypes.number,
    Name: PropTypes.string,
  }),
  // Optional global logout callback coming from TabsHomePage (handleLogout)
  onForceLogout: PropTypes.func,
};
